import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart, Users, ShoppingBag, Mail, DollarSign, LogOut,
  Trash2, ChevronDown, ChevronUp, Clock, Bell, Check, X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
  { id: "overview", label: "Overview", icon: Heart },
  { id: "pantry", label: "Pantry Requests", icon: ShoppingBag },
  { id: "volunteers", label: "Volunteers", icon: Users },
  { id: "contacts", label: "Messages", icon: Mail },
  { id: "donations", label: "Donations", icon: DollarSign },
];

function TimeAgo({ date }) {
  if (!date) return null;
  const d = new Date(date);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return <span>{diff}s ago</span>;
  if (diff < 3600) return <span>{Math.floor(diff / 60)}m ago</span>;
  if (diff < 86400) return <span>{Math.floor(diff / 3600)}h ago</span>;
  return <span>{d.toLocaleDateString()}</span>;
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#F2EFEB] border border-[#E8E3DD] rounded-2xl p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon className={`w-6 h-6 mb-3 ${color}`} />
      <p className="font-heading text-3xl text-[#2C2C2A] font-medium">{value}</p>
      <p className="font-body text-xs text-[#6C6B66] mt-1">{label}</p>
    </div>
  );
}

function DataTable({ data, columns, onDelete, emptyMsg }) {
  const [expandedRow, setExpandedRow] = useState(null);

  if (!data.length) {
    return <p className="font-body text-sm text-[#6C6B66] py-8 text-center">{emptyMsg}</p>;
  }

  return (
    <div className="space-y-3">
      {data.map((row, i) => (
        <div key={row.id || i} className="bg-[#F2EFEB] border border-[#E8E3DD] rounded-2xl overflow-hidden" data-testid={`table-row-${i}`}>
          {/* Summary row */}
          <div
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#E8E3DD]/30 transition-colors"
            onClick={() => setExpandedRow(expandedRow === i ? null : i)}
          >
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {columns.slice(0, 3).map((col) => (
                <span key={col.key} className={`font-body text-sm ${col.key === columns[0].key ? "font-semibold text-[#2C2C2A]" : "text-[#6C6B66]"} truncate`}>
                  {col.render ? col.render(row[col.key], row) : row[col.key] || "—"}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="font-body text-xs text-[#6C6B66] flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <TimeAgo date={row.created_at} />
              </span>
              {expandedRow === i ? <ChevronUp className="w-4 h-4 text-[#6C6B66]" /> : <ChevronDown className="w-4 h-4 text-[#6C6B66]" />}
            </div>
          </div>

          {/* Expanded details */}
          {expandedRow === i && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-[#E8E3DD] px-4 py-4 bg-white/50"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {columns.map((col) => (
                  <div key={col.key}>
                    <p className="font-body text-[10px] uppercase tracking-wider text-[#6C6B66] mb-0.5">{col.label}</p>
                    <p className="font-body text-sm text-[#3A3A38]">
                      {col.render ? col.render(row[col.key], row) : row[col.key] || "—"}
                    </p>
                  </div>
                ))}
              </div>
              {onDelete && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(row.id); }}
                  className="mt-4 flex items-center gap-1 font-body text-xs text-red-500 hover:text-red-700 transition-colors"
                  data-testid={`delete-row-${i}`}
                >
                  <Trash2 className="w-3 h-3" /> Remove
                </button>
              )}
            </motion.div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [pantryRequests, setPantryRequests] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const navigate = useNavigate();

  const fetchUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (res.data.role !== "admin") {
        navigate("/admin");
        return;
      }
      setUser(res.data);
    } catch {
      navigate("/admin");
    }
  }, [navigate]);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifsRes, countRes] = await Promise.all([
        axios.get(`${API}/admin/notifications`, { withCredentials: true }),
        axios.get(`${API}/admin/notifications/unread-count`, { withCredentials: true }),
      ]);
      setNotifications(notifsRes.data);
      setUnreadCount(countRes.data.count);
    } catch { /* silent */ }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, pantryRes, volRes, contactRes, donRes] = await Promise.all([
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/pantry-requests`, { withCredentials: true }),
        axios.get(`${API}/admin/volunteers`, { withCredentials: true }),
        axios.get(`${API}/admin/contacts`, { withCredentials: true }),
        axios.get(`${API}/admin/donations`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setPantryRequests(pantryRes.data);
      setVolunteers(volRes.data);
      setContacts(contactRes.data);
      setDonations(donRes.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) navigate("/admin");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchUser(); }, [fetchUser]);
  useEffect(() => { if (user) { fetchData(); fetchNotifications(); } }, [user, fetchData, fetchNotifications]);

  // Poll for new notifications every 30s
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/admin/notifications/mark-read`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch { toast.error("Could not update notifications"); }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate("/admin");
  };

  const handleDeletePantry = async (id) => {
    try {
      await axios.delete(`${API}/admin/pantry-requests/${id}`, { withCredentials: true });
      setPantryRequests((prev) => prev.filter((r) => r.id !== id));
      toast.success("Request removed");
    } catch { toast.error("Could not delete"); }
  };

  const handleDeleteVolunteer = async (id) => {
    try {
      await axios.delete(`${API}/admin/volunteers/${id}`, { withCredentials: true });
      setVolunteers((prev) => prev.filter((r) => r.id !== id));
      toast.success("Volunteer removed");
    } catch { toast.error("Could not delete"); }
  };

  const handleDeleteContact = async (id) => {
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { withCredentials: true });
      setContacts((prev) => prev.filter((r) => r.id !== id));
      toast.success("Message removed");
    } catch { toast.error("Could not delete"); }
  };

  if (!user) return null;

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#FDFBF7]">
      {/* Top bar */}
      <div className="bg-[#2C2C2A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-14">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#C0603A] fill-[#C0603A]" />
            <span className="font-heading text-lg font-medium">333Oreo Admin</span>
          </div>
          <div className="flex items-center gap-4">
            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifPanel(!showNotifPanel)}
                className="relative p-1.5 text-white/60 hover:text-white transition-colors"
                data-testid="notification-bell"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#C0603A] text-white text-[9px] font-bold w-4.5 h-4.5 min-w-[18px] rounded-full flex items-center justify-center px-1" data-testid="notification-badge">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification dropdown panel */}
              {showNotifPanel && (
                <div className="absolute right-0 top-10 w-80 sm:w-96 bg-white border border-[#E8E3DD] rounded-2xl shadow-[0_12px_48px_rgba(44,44,42,0.15)] z-50 overflow-hidden" data-testid="notification-panel">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8E3DD] bg-[#F2EFEB]">
                    <span className="font-body text-sm font-semibold text-[#2C2C2A]">Alerts</span>
                    <div className="flex items-center gap-2">
                      {unreadCount > 0 && (
                        <button
                          onClick={handleMarkAllRead}
                          className="font-body text-[10px] text-[#C0603A] hover:text-[#A64D29] font-medium flex items-center gap-1"
                          data-testid="mark-all-read-button"
                        >
                          <Check className="w-3 h-3" /> Mark all read
                        </button>
                      )}
                      <button onClick={() => setShowNotifPanel(false)} className="text-[#6C6B66] hover:text-[#2C2C2A]">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="font-body text-sm text-[#6C6B66] text-center py-8">No alerts yet</p>
                    ) : (
                      notifications.map((n, i) => (
                        <div
                          key={n.id || i}
                          className={`px-4 py-3 border-b border-[#E8E3DD]/50 last:border-0 ${!n.read ? "bg-[#C0603A]/[0.03]" : ""}`}
                          data-testid={`notification-item-${i}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.read ? "bg-[#C0603A]" : "bg-[#E8E3DD]"}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {n.type === "pantry_request" && <ShoppingBag className="w-3.5 h-3.5 text-[#C0603A] flex-shrink-0" />}
                                {n.type === "volunteer_signup" && <Users className="w-3.5 h-3.5 text-[#8FA396] flex-shrink-0" />}
                                {n.type === "contact_message" && <Mail className="w-3.5 h-3.5 text-[#6C6B66] flex-shrink-0" />}
                                {n.type === "donation_received" && <DollarSign className="w-3.5 h-3.5 text-[#C0603A] flex-shrink-0" />}
                                <p className="font-body text-xs font-semibold text-[#2C2C2A] truncate">{n.title}</p>
                              </div>
                              <p className="font-body text-[11px] text-[#6C6B66] mt-0.5 leading-relaxed line-clamp-2">{n.message}</p>
                              <p className="font-body text-[10px] text-[#6C6B66]/60 mt-1"><TimeAgo date={n.created_at} /></p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <span className="font-body text-xs text-white/60">{user.email}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1 font-body text-xs text-white/60 hover:text-white transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Tab navigation */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-testid="admin-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-[#C0603A] text-white"
                  : "bg-[#F2EFEB] text-[#6C6B66] hover:text-[#2C2C2A] hover:bg-[#E8E3DD]"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === "pantry" && pantryRequests.length > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? "bg-white/20" : "bg-[#C0603A]/10 text-[#C0603A]"}`}>
                  {pantryRequests.length}
                </span>
              )}
              {tab.id === "volunteers" && volunteers.length > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? "bg-white/20" : "bg-[#C0603A]/10 text-[#C0603A]"}`}>
                  {volunteers.length}
                </span>
              )}
              {tab.id === "contacts" && contacts.length > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? "bg-white/20" : "bg-[#C0603A]/10 text-[#C0603A]"}`}>
                  {contacts.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-[#C0603A] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <>
            {/* Overview */}
            {activeTab === "overview" && stats && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="font-heading text-2xl text-[#2C2C2A] mb-6">Dashboard Overview</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <StatCard icon={ShoppingBag} label="Pantry Requests" value={stats.pantry_requests} color="text-[#C0603A]" />
                  <StatCard icon={Users} label="Volunteers" value={stats.volunteers} color="text-[#8FA396]" />
                  <StatCard icon={DollarSign} label="Total Raised" value={`$${stats.total_raised.toFixed(2)}`} color="text-[#C0603A]" />
                  <StatCard icon={Heart} label="Donations" value={stats.donations_count} color="text-[#8FA396]" />
                </div>

                {/* Recent activity */}
                <h3 className="font-heading text-xl text-[#2C2C2A] mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {[...pantryRequests.slice(0, 2).map(r => ({ type: "pantry", ...r })),
                    ...volunteers.slice(0, 2).map(r => ({ type: "volunteer", ...r })),
                    ...contacts.slice(0, 2).map(r => ({ type: "contact", ...r })),
                  ]
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .slice(0, 5)
                    .map((item, i) => (
                      <div key={i} className="bg-[#F2EFEB] border border-[#E8E3DD] rounded-xl p-4 flex items-center gap-4" data-testid={`recent-activity-${i}`}>
                        {item.type === "pantry" && <ShoppingBag className="w-5 h-5 text-[#C0603A] flex-shrink-0" />}
                        {item.type === "volunteer" && <Users className="w-5 h-5 text-[#8FA396] flex-shrink-0" />}
                        {item.type === "contact" && <Mail className="w-5 h-5 text-[#6C6B66] flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="font-body text-sm font-semibold text-[#2C2C2A] truncate">{item.name}</p>
                          <p className="font-body text-xs text-[#6C6B66]">
                            {item.type === "pantry" && "Pet Pantry Request"}
                            {item.type === "volunteer" && "Volunteer Signup"}
                            {item.type === "contact" && "Contact Message"}
                          </p>
                        </div>
                        <span className="font-body text-xs text-[#6C6B66]"><TimeAgo date={item.created_at} /></span>
                      </div>
                    ))}
                  {pantryRequests.length === 0 && volunteers.length === 0 && contacts.length === 0 && (
                    <p className="font-body text-sm text-[#6C6B66] text-center py-8">No activity yet. Submissions will appear here.</p>
                  )}
                </div>
              </motion.div>
            )}

            {/* Pantry Requests */}
            {activeTab === "pantry" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="font-heading text-2xl text-[#2C2C2A] mb-6">
                  Pet Pantry Requests <span className="text-[#6C6B66] text-lg">({pantryRequests.length})</span>
                </h2>
                <DataTable
                  data={pantryRequests}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "pet_type", label: "Pet Type" },
                    { key: "pet_name", label: "Pet Name" },
                    { key: "phone", label: "Phone" },
                    { key: "address", label: "Location" },
                    { key: "needs", label: "Needs" },
                    { key: "message", label: "Message" },
                    { key: "created_at", label: "Submitted", render: (v) => v ? new Date(v).toLocaleString() : "—" },
                  ]}
                  onDelete={handleDeletePantry}
                  emptyMsg="No pantry requests yet."
                />
              </motion.div>
            )}

            {/* Volunteers */}
            {activeTab === "volunteers" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="font-heading text-2xl text-[#2C2C2A] mb-6">
                  Volunteers <span className="text-[#6C6B66] text-lg">({volunteers.length})</span>
                </h2>
                <DataTable
                  data={volunteers}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "phone", label: "Phone" },
                    { key: "skills", label: "Skills" },
                    { key: "availability", label: "Availability" },
                    { key: "message", label: "Message" },
                    { key: "created_at", label: "Signed Up", render: (v) => v ? new Date(v).toLocaleString() : "—" },
                  ]}
                  onDelete={handleDeleteVolunteer}
                  emptyMsg="No volunteers yet."
                />
              </motion.div>
            )}

            {/* Contacts */}
            {activeTab === "contacts" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="font-heading text-2xl text-[#2C2C2A] mb-6">
                  Contact Messages <span className="text-[#6C6B66] text-lg">({contacts.length})</span>
                </h2>
                <DataTable
                  data={contacts}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "subject", label: "Subject" },
                    { key: "message", label: "Message" },
                    { key: "created_at", label: "Received", render: (v) => v ? new Date(v).toLocaleString() : "—" },
                  ]}
                  onDelete={handleDeleteContact}
                  emptyMsg="No messages yet."
                />
              </motion.div>
            )}

            {/* Donations */}
            {activeTab === "donations" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="font-heading text-2xl text-[#2C2C2A] mb-6">
                  Donations <span className="text-[#6C6B66] text-lg">({donations.length})</span>
                </h2>
                <DataTable
                  data={donations}
                  columns={[
                    { key: "package_name", label: "Package" },
                    { key: "amount", label: "Amount", render: (v) => v ? `$${Number(v).toFixed(2)}` : "—" },
                    { key: "payment_status", label: "Status", render: (v) => (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        v === "paid" ? "bg-green-100 text-green-700" : v === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-600"
                      }`}>{v || "unknown"}</span>
                    )},
                    { key: "currency", label: "Currency", render: (v) => (v || "usd").toUpperCase() },
                    { key: "session_id", label: "Session ID" },
                    { key: "created_at", label: "Date", render: (v) => v ? new Date(v).toLocaleString() : "—" },
                  ]}
                  emptyMsg="No donations yet."
                />
              </motion.div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
